// ===============================
// AXOIX Features/Services Page
// Using EXACT theme structure from components/ServiceSection
// ===============================

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Theme images
import aboutBg from "../../images/bg/service-bg.png";
import gif from "../../images/icon/b10c3e43e836d32554bf.gif";
import arrowIcon from "../../images/icon/rotate-arrow-black.svg";

// Service images — matched to each module
import imgAicosting from "../../images/service/aicosting.webp";
import imgSeal from "../../images/service/seal.webp";
import imgChorus from "../../images/service/chorus.webp";
import imgCrm from "../../images/service/crm.webp";
import imgLink from "../../images/service/link.webp";
import imgRemedy from "../../images/service/remedy.webp";

interface ServiceItem {
  id: number;
  titleKey: string;
  descKey: string;
  img: string;
  link: string;
}

// AXOIX Modules data - uses translation keys
const services: ServiceItem[] = [
  {
    id: 1,
    titleKey: "services.modules.industryModules.title",
    descKey: "services.modules.industryModules.desc",
    img: imgAicosting,
    link: "#industries",
  },
  {
    id: 2,
    titleKey: "services.modules.voiceCommand.title",
    descKey: "services.modules.voiceCommand.desc",
    img: imgChorus,
    link: "/team",
  },
  {
    id: 3,
    titleKey: "services.modules.linkPhone.title",
    descKey: "services.modules.linkPhone.desc",
    img: imgLink,
    link: "/pricing",
  },
  {
    id: 4,
    titleKey: "services.modules.aiCharacters.title",
    descKey: "services.modules.aiCharacters.desc",
    img: imgCrm,
    link: "/team",
  },
  {
    id: 5,
    titleKey: "services.modules.crm.title",
    descKey: "services.modules.crm.desc",
    img: imgSeal,
    link: "/team",
  },
  {
    id: 6,
    titleKey: "services.modules.erp.title",
    descKey: "services.modules.erp.desc",
    img: imgRemedy,
    link: "/team",
  },
];

const ServiceSection: React.FC = () => {
  const { t } = useTranslation();
  const [activeId, setActiveId] = useState<number>(1);

  useEffect(() => {
    const bgEl = document.querySelector<HTMLElement>(".xb-service-wrap");
    if (bgEl) bgEl.style.backgroundImage = `url(${aboutBg})`;
  }, []);

  return (
    <section className="service pt-135">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-11">
            <div className="sec-title custom-sec-title xb-sec-padding text-center">
              <span className="sub-title">{t('services.subtitle')}</span>
              <h2 className="title">
                <span className="round-img">
                  <img src={gif} alt="gif" />
                </span>{" "}
                {t('services.title')}
              </h2>
              <p className="mt-20 mb-40 opacity-75 service-intro-text">
                {t('services.description')}
              </p>
              <div className="xb-heading-btn d-inline">
                <Link className="thm-btn agency-btn" to="/team">
                  <span className="text">{t('services.meetCharacters')}</span>
                  <span className="arrow">
                    <span className="arrow-icon">
                      <svg
                        width="28"
                        height="28"
                        viewBox="0 0 28 28"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect x="5.06592" y="19.9785" width="20.5712" height="2.61221" transform="rotate(-40.2798 5.06592 19.9785)" fill="white"/>
                        <rect x="7.97095" y="7.24463" width="2.61221" height="2.61221" transform="rotate(-40.2798 7.97095 7.24463)" fill="white"/>
                        <rect x="11.6523" y="7.54834" width="2.61221" height="2.61221" transform="rotate(-40.2798 11.6523 7.54834)" fill="white"/>
                        <rect x="15.334" y="7.85205" width="2.61221" height="2.61221" transform="rotate(-40.2798 15.334 7.85205)" fill="white"/>
                        <rect x="18.7119" y="11.8374" width="2.61221" height="2.61221" transform="rotate(-40.2798 18.7119 11.8374)" fill="white"/>
                        <rect x="18.4084" y="15.52" width="2.61221" height="2.61221" transform="rotate(-40.2798 18.4084 15.52)" fill="white"/>
                        <rect x="18.104" y="19.2012" width="2.61221" height="2.61221" transform="rotate(-40.2798 18.104 19.2012)" fill="white"/>
                      </svg>
                      <svg
                        width="28"
                        height="28"
                        viewBox="0 0 28 28"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect x="5.06592" y="19.9785" width="20.5712" height="2.61221" transform="rotate(-40.2798 5.06592 19.9785)" fill="white"/>
                        <rect x="7.97095" y="7.24463" width="2.61221" height="2.61221" transform="rotate(-40.2798 7.97095 7.24463)" fill="white"/>
                        <rect x="11.6523" y="7.54834" width="2.61221" height="2.61221" transform="rotate(-40.2798 11.6523 7.54834)" fill="white"/>
                        <rect x="15.334" y="7.85205" width="2.61221" height="2.61221" transform="rotate(-40.2798 15.334 7.85205)" fill="white"/>
                        <rect x="18.7119" y="11.8374" width="2.61221" height="2.61221" transform="rotate(-40.2798 18.7119 11.8374)" fill="white"/>
                        <rect x="18.4084" y="15.52" width="2.61221" height="2.61221" transform="rotate(-40.2798 18.4084 15.52)" fill="white"/>
                        <rect x="18.104" y="19.2012" width="2.61221" height="2.61221" transform="rotate(-40.2798 18.104 19.2012)" fill="white"/>
                      </svg>
                    </span>
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Service Boxes - Theme structure */}
      <div className="xb-service-wrap bg_img">
        {services.map((service) => (
          <div
            key={service.id}
            className={`xb-service-item xb-border xb-mouseenter ${
              activeId === service.id ? "active" : ""
            }`}
            onMouseEnter={() => setActiveId(service.id)}
          >
            <div className="xb-item--inner">
              <div className="xb-item--item">
                <div className="xb-item--head-item">
                  <h3 className="xb-item--title border-effect">
                    <Link to={service.link}>{t(service.titleKey)}</Link>
                  </h3>
                  <Link className="xb-item--icon" to={service.link}>
                    <img src={arrowIcon} alt="arrow" />
                  </Link>
                </div>
                <p className="xb-item--content">{t(service.descKey)}</p>
                <div className="img-hove-effect">
                  <div className="xb-item--img xb-img">
                    {[...Array(4)].map((_, i) => (
                      <Link to={service.link} key={i}>
                        <img src={service.img} alt={t(service.titleKey)} />
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              <div className="service-vertical-text">
                <h3 className="xb-item--title">
                  <Link to={service.link}>{t(service.titleKey)}</Link>
                </h3>
                <Link className="xb-icon" to={service.link}>
                  <svg
                    width="31"
                    height="31"
                    viewBox="0 0 31 31"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect x="6.28979" y="21.4111" width="22.36" height="2.83936" transform="rotate(-40.2798 6.28979 21.4111)" fill="white"/>
                    <rect x="9.44751" y="7.57031" width="2.83936" height="2.83936" transform="rotate(-40.2798 9.44751 7.57031)" fill="white"/>
                    <rect x="13.449" y="7.90015" width="2.83936" height="2.83936" transform="rotate(-40.2798 13.449 7.90015)" fill="white"/>
                    <rect x="17.4507" y="8.23047" width="2.83936" height="2.83936" transform="rotate(-40.2798 17.4507 8.23047)" fill="white"/>
                    <rect x="21.1223" y="12.5627" width="2.83936" height="2.83936" transform="rotate(-40.2798 21.1223 12.5627)" fill="white"/>
                    <rect x="20.7925" y="16.5649" width="2.83936" height="2.83936" transform="rotate(-40.2798 20.7925 16.5649)" fill="white"/>
                    <rect x="20.4617" y="20.5667" width="2.83936" height="2.83936" transform="rotate(-40.2798 20.4617 20.5667)" fill="white"/>
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default ServiceSection;
